import React, { createContext, useState, useEffect, useMemo, useContext } from 'react';
import api from '../api/client';
import { LoginContext } from './loginContext';

export const PermissionsContext = createContext();

const RUTAS_ADMIN_ORDEN = [
  ['inicio', 'ver', '/administrador/home'],
  ['clientes', 'ver', '/administrador/clientes'],
  ['productos', 'ver', '/administrador/productos'],
  ['conceptos', 'ver', '/administrador/conceptos'],
  ['exportacion', 'ver', '/administrador/exportacion'],
  ['importacion', 'ver', '/administrador/importacion'],
  ['servicios_acordados', 'ver', '/administrador/servicios-acordados'],
  ['cotizaciones_eliminadas', 'ver', '/administrador/cotizaciones-eliminadas'],
  ['agenda_reuniones', 'ver', '/administrador/agenda-reuniones'],
  ['curriculums', 'ver', '/administrador/curriculums'],
  ['localidades', 'ver', '/administrador/localidades'],
  ['usuarios', 'ver', '/administrador/usuarios'],
];

export const PermissionsProvider = ({ children }) => {
  const { user, isAuthenticated } = useContext(LoginContext);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setPermissions([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .get('/usuarios/me/permissions')
      .then(({ data }) => {
        if (!cancelled) {
          setPermissions(Array.isArray(data?.permissions) ? data.permissions : []);
        }
      })
      .catch(() => {
        if (!cancelled) setPermissions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user?.usu_id, user?.id]);

  const hasPermission = useMemo(
    () => (modulo, accion) => {
      if (!Array.isArray(permissions)) return false;
      if (permissions.includes('*')) return true;
      const code = accion ? `${modulo}.${accion}` : modulo;
      return permissions.includes(code);
    },
    [permissions]
  );

  const getFirstAdminPath = useMemo(
    () => () => {
      for (const [modulo, accion, path] of RUTAS_ADMIN_ORDEN) {
        if (hasPermission(modulo, accion)) return path;
      }
      return '/administrador/home';
    },
    [hasPermission]
  );

  const value = useMemo(
    () => ({ permissions, loading, hasPermission, getFirstAdminPath }),
    [permissions, loading, hasPermission, getFirstAdminPath]
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};

export const usePermissions = () => {
  const ctx = useContext(PermissionsContext);
  if (!ctx) throw new Error('usePermissions debe usarse dentro de PermissionsProvider');
  return ctx;
};
